import { createColumnHelper } from "@tanstack/react-table";
import { formatDate } from "date-fns";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Button, Icon, Modal } from "semantic-ui-react";

import { AppointmentStatusCell, DATE_FORMATS_FNS, ReactTable, useCan, useSelectedLocation } from "components";
import { ACL_ACTIVITIES, ACL_RESOURCES, STATUS_IDENTIFIERS } from "models";
import { EmptyKeyLockerBoxModal, KeyLockerBoxPinModal, OpenKeyLockerBoxModal } from "modules/KeyLockersDashboard/components/KeyLockerBoxesModal/components";
import {
  useActivateKeyLockerBoxMutation,
  useDeactivateKeyLockerBoxMutation,
  useKeyLockerDetailsQuery
} from "modules/KeyLockersDashboard/components/KeyLockerBoxesModal/hooks";
import "modules/KeyLockersDashboard/components/KeyLockerBoxesModal/KeyLockerBoxesModal.scss";
import { PATHS } from "router/paths";

import type { KeyLockerBox } from "models";

type KeyLockerBoxesModalProps = {
  keyLockerId: number | null;
  onClose: () => void;
  keyLockerName?: string;
};

const columnHelper = createColumnHelper<KeyLockerBox>();
const FINISHED_STATUSES = [STATUS_IDENTIFIERS.CanceledStatus, STATUS_IDENTIFIERS.CarReadyStatus, STATUS_IDENTIFIERS.QualityCheckStatus];

export const KeyLockerBoxesModal = ({ keyLockerId, onClose, keyLockerName }: KeyLockerBoxesModalProps) => {
  const { t } = useTranslation();

  const [showPinModal, setShowPinModal] = useState("");
  const [keyLockerBoxLockId, setKeyLockerBoxLockId] = useState(0);
  const [emptyKeyLockerBoxId, setEmptyKeyLockerBoxId] = useState(0);
  const [activateDeactivateKeyLockerId, setActivateDeactivateKeyLockerId] = useState(0);

  const canOpenLock = useCan(ACL_ACTIVITIES.List, ACL_RESOURCES.KeylockersAdminPin);
  const canUpdateKeyLockerBox = useCan(ACL_ACTIVITIES.Update, ACL_RESOURCES.KeylockersBox);

  const { selectedLocation } = useSelectedLocation();
  const { data: keyLockerDetails, isFetching: isFetchingKeyLockerDetails } = useKeyLockerDetailsQuery(keyLockerId);
  const { mutate: activateKeyLockerBox, isPending: isActivateKeyLockerBoxPending } = useActivateKeyLockerBoxMutation();
  const { mutate: deactivateKeyLockerBox, isPending: isDeactivateKeyLockerBoxPending } = useDeactivateKeyLockerBoxMutation();

  useEffect(() => {
    if (!isActivateKeyLockerBoxPending && !isDeactivateKeyLockerBoxPending) setActivateDeactivateKeyLockerId(0);
  }, [isActivateKeyLockerBoxPending, isDeactivateKeyLockerBoxPending]);

  if (!keyLockerId) return null;

  const handleCloseKeyLockerBoxLock = () => setKeyLockerBoxLockId(0);
  const handleOpenKeyLockerBoxLockModal = (id: number) => setKeyLockerBoxLockId(id);

  const handleCloseKeyLockerBoxEmptyModal = () => setEmptyKeyLockerBoxId(0);
  const handleOpenKeyLockerBoxEmptyModal = (id: number) => setEmptyKeyLockerBoxId(id);

  const columns = [
    columnHelper.accessor(row => row, {
      id: "box",
      size: 30,
      header: () => <span className="header-title box-index">#</span>,
      cell: ({
        row: {
          original: { box_number, active }
        }
      }) => (
        <div className={`box-container ${active ? "active-box" : "disabled-box"}`}>
          <span className={active ? "active-box-text" : "disabled-box-text"}>{box_number}</span>
        </div>
      )
    }),
    columnHelper.accessor(row => row, {
      id: "key",
      size: 30,
      header: () => <span className="header-title">{t("v8_key", "Key")}</span>,
      cell: ({
        row: {
          original: { is_empty }
        }
      }) => (
        <div className="key-container">
          <Icon name={is_empty ? "close" : "key"} className={is_empty ? "key-container-close" : ""} />
        </div>
      )
    }),
    columnHelper.accessor(row => row, {
      id: "status",
      size: 30,
      header: () => <span className="header-title"> {t("v8_status", "Status")} </span>,
      cell: ({
        row: {
          original: { appointment }
        }
      }) => {
        if (!appointment) return null;

        return (
          <AppointmentStatusCell
            shadow={false}
            value={{
              id: appointment.appointment_status_identifier,
              customcom_status: appointment.customer_communication?.status,
              car_check_paused_at: appointment.car_check_paused_at,
              car_out_of_shop: appointment.car_out_of_shop,
              car_in_shop: appointment.car_in_shop
            }}
            statuses={selectedLocation?.statuses}
          />
        );
      }
    }),
    columnHelper.accessor(row => row, {
      id: "wo",
      size: 100,
      header: () => <span className="header-title"> {t("v8_wo", "WO")} </span>,
      cell: ({
        row: {
          original: { appointment }
        }
      }) => {
        if (!appointment?.id) return null;

        return (
          <span className="header-title">
            <Link to={`${PATHS.APPOINTMENTS}/${appointment.id}`} target="_blank">
              <Icon className="arrow up right from square" />
              {appointment.wo_nr}
            </Link>
          </span>
        );
      }
    }),
    columnHelper.accessor(row => row, {
      id: "appointment_date",
      size: 80,
      header: () => <span className="header-title"> {t("v8_appointment_date", "Appointment Date")} </span>,
      cell: ({
        row: {
          original: { appointment }
        }
      }) => <span> {appointment ? formatDate(appointment.time_car_app, DATE_FORMATS_FNS.dateMonthYear) : ""} </span>
    }),
    columnHelper.accessor(row => row, {
      id: "driver_name",
      size: 100,
      header: () => <span className="header-title"> {t("v8_driver_name", "Driver Name")} </span>,
      cell: ({
        row: {
          original: { appointment }
        }
      }) => <span className="header-title"> {appointment ? `${appointment.driver_firstname} ${appointment.driver_surname}` : ""} </span>
    }),
    columnHelper.accessor(row => row, {
      id: "owner_name",
      size: 100,
      header: () => <span className="header-title"> {t("v8_owner_name", "Owner Name")} </span>,
      cell: ({
        row: {
          original: { appointment }
        }
      }) => <span className="header-title"> {appointment ? `${appointment.owner_firstname} ${appointment.owner_surname}` : ""} </span>
    }),
    columnHelper.accessor(row => row, {
      id: "actions",
      size: 50,
      header: () => "",
      cell: ({
        row: {
          original: { active, id, is_empty, appointment }
        }
      }) => {
        const canToggleKeyLockerBox = canUpdateKeyLockerBox && (!active || is_empty);
        const canEmptyKeyLockerBox = canUpdateKeyLockerBox && !is_empty && appointment && FINISHED_STATUSES.includes(appointment.appointment_status_identifier);

        return (
          <div className="actions-container">
            <div className="action-button-container" onClick={() => canOpenLock && handleOpenKeyLockerBoxLockModal(id)}>
              <Icon className="lock open action-button-icon-center" color="blue" disabled={!canOpenLock} />
            </div>
            <div
              className="action-button-container"
              onClick={() => {
                if (!canToggleKeyLockerBox) return;
                setActivateDeactivateKeyLockerId(id);
                active ? deactivateKeyLockerBox(id) : activateKeyLockerBox(id);
              }}
            >
              <Icon
                className={`${active ? "circle pause" : "play"} action-button-icon-center`}
                color={active ? "red" : "green"}
                disabled={!canToggleKeyLockerBox || (id === activateDeactivateKeyLockerId && (isActivateKeyLockerBoxPending || isDeactivateKeyLockerBoxPending))}
              />
            </div>

            <div className="action-button-container" onClick={() => canEmptyKeyLockerBox && handleOpenKeyLockerBoxEmptyModal(id)}>
              <Icon className="close action-button-icon-center empty-box-icon" color="red" disabled={!canEmptyKeyLockerBox} />
            </div>
          </div>
        );
      }
    })
  ];

  return (
    <>
      <Modal size="large" open={Boolean(keyLockerId)} closeOnDimmerClick onClose={onClose} className="KeyLockerBoxesModal">
        <Modal.Header className="header-container">
          <div className="header-button-container">
            {keyLockerName || ""}
            <Button className="modal-close-btn" basic onClick={onClose}>
              <Icon className="xmark" />
            </Button>
          </div>
        </Modal.Header>
        <Modal.Content>
          <ReactTable isFetching={isFetchingKeyLockerDetails} columns={columns} data={keyLockerDetails} />
        </Modal.Content>
      </Modal>

      <OpenKeyLockerBoxModal id={keyLockerBoxLockId} resetKeyLockerBoxId={handleCloseKeyLockerBoxLock} openPinModal={(pin: string) => setShowPinModal(pin)} />
      <EmptyKeyLockerBoxModal id={emptyKeyLockerBoxId} resetKeyLockerBoxId={handleCloseKeyLockerBoxEmptyModal} />
      <KeyLockerBoxPinModal isOpen={Boolean(showPinModal)} onClose={() => setShowPinModal("")} pinNumber={showPinModal} />
    </>
  );
};
